/**
 * /settings — workspace, members, API keys, danger zone.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Key, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CopyField } from '@/components/patterns/copy-field';
import { PageHeader } from '@/components/patterns/page-header';
import { SectionCard } from '@/components/patterns/section-card';
import { StatusPill } from '@/components/patterns/status-pill';
import { DomainPanel } from '@/components/settings/domain-panel';
import { MembersPanel } from '@/components/settings/members-panel';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, type ApiKey, MCP_FAMILIES, mcpKeysApi, workspaceApi } from '@/lib/api';
import { authClient } from '@/lib/auth-client';
import { IANA_TIMEZONES } from '@/lib/constants';
import { qk } from '@/lib/query-client';
import { useActiveOrg } from '@/lib/use-active-org';
import { browserTimezone, mcpEndpoint } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Workspace section
// ---------------------------------------------------------------------------

function WorkspaceSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: qk.workspace, queryFn: () => workspaceApi.get() });
  const workspace = data?.workspace;

  const [name, setName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [timezone, setTimezone] = useState(browserTimezone());

  const renameMutation = useMutation({
    mutationFn: (n: string) => workspaceApi.patch({ name: n }),
    onSuccess: (res) => {
      qc.setQueryData(qk.workspace, res);
      toast.success('Workspace renamed');
      setEditingName(false);
    },
    onError: () => toast.error('Failed to rename'),
  });

  const tzMutation = useMutation({
    mutationFn: (tz: string) => workspaceApi.patch({ timezone: tz }),
    onSuccess: (res) => {
      qc.setQueryData(qk.workspace, res);
      toast.success('Timezone saved');
    },
    onError: () => toast.error('Failed to save timezone'),
  });

  return (
    <SectionCard title="Workspace" description="Name and timezone for your workspace.">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
        {/* Name */}
        <div className="space-y-1.5">
          <Label>Workspace name</Label>
          {editingName ? (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) renameMutation.mutate(name.trim());
              }}
            >
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                maxLength={120}
              />
              <Button type="submit" size="sm" disabled={renameMutation.isPending}>
                {renameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingName(false)}>
                Cancel
              </Button>
            </form>
          ) : workspace ? (
            <div className="flex items-center gap-3">
              <span className="text-sm">{workspace.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setName(workspace.name);
                  setEditingName(true);
                }}
              >
                Rename
              </Button>
            </div>
          ) : (
            <Skeleton className="h-9 w-48" />
          )}
        </div>

        {/* Timezone */}
        <div className="space-y-1.5">
          <Label htmlFor="ws-tz">Default timezone</Label>
          <div className="flex gap-2">
            <Select
              id="ws-tz"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="flex-1"
            >
              {IANA_TIMEZONES.length === 0 ? (
                <option value={timezone}>{timezone}</option>
              ) : (
                IANA_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))
              )}
            </Select>
            <Button
              size="sm"
              variant="outline"
              disabled={tzMutation.isPending}
              onClick={() => tzMutation.mutate(timezone)}
            >
              {tzMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// API keys section
// ---------------------------------------------------------------------------

function ApiKeyRow({ apiKey, onRevoke }: { apiKey: ApiKey; onRevoke: (id: string) => void }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex items-center gap-3 py-3">
      <Key className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{apiKey.name ?? 'Unnamed key'}</p>
        <p className="font-mono text-[13px] text-faint">
          {apiKey.prefix ?? ''}••••{apiKey.start ? ` (starts: ${apiKey.start})` : ''} · created{' '}
          {new Date(apiKey.createdAt).toLocaleDateString()}
          {apiKey.lastRequest &&
            ` · last used ${new Date(apiKey.lastRequest).toLocaleDateString()}`}
        </p>
      </div>
      <StatusPill tone={apiKey.enabled ? 'active' : 'neutral'}>
        {apiKey.enabled ? 'Active' : 'Disabled'}
      </StatusPill>
      {confirming ? (
        <div className="flex gap-1">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              onRevoke(apiKey.id);
              setConfirming(false);
            }}
          >
            Revoke
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
          onClick={() => setConfirming(true)}
          aria-label="Revoke key"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function ApiKeysSection() {
  const qc = useQueryClient();
  const [newKeyName, setNewKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  // Own query rather than relying on WorkspaceSection's: that section is
  // owner-only and this one isn't, so a member needs its own fetch (React
  // Query dedupes it against WorkspaceSection's identical key when both are
  // mounted for an owner).
  const { data: wsData } = useQuery({ queryKey: qk.workspace, queryFn: () => workspaceApi.get() });
  const workspace = wsData?.workspace;
  const orgEndpoint = workspace ? mcpEndpoint({ slug: workspace.slug }) : null;
  const legacyEndpoint = mcpEndpoint();

  // Gates which source-scoped URLs are offered below (AC22). Membership comes
  // straight from the server (`workspace.connectedFamilies`) rather than being
  // inferred client-side: imported-only sources (whatsapp_export/signal_export)
  // have a `sources` row but no connector account, so a client-side guess from
  // account-list endpoints would silently miss them.
  // `?? []` is a deploy-skew guard, not a design choice: this crashed a render
  // outright when a test fixture predated the field (settings-role-sections),
  // and a rolling deploy can put an old API response behind a new bundle for a
  // few minutes the same way.
  const scopedFamilies = workspace
    ? MCP_FAMILIES.filter((f) => (workspace.connectedFamilies ?? []).includes(f.slug))
    : [];

  const { data, isLoading } = useQuery({
    queryKey: qk.mcpKeys,
    queryFn: () => mcpKeysApi.list(),
  });

  const createKey = useMutation({
    mutationFn: (name: string) => mcpKeysApi.create({ name }),
    onSuccess: (res) => {
      setRevealedKey(res.key);
      setNewKeyName('');
      void qc.invalidateQueries({ queryKey: qk.mcpKeys });
      toast.success("API key created — copy it now, it won't be shown again");
    },
    onError: () => toast.error('Failed to create API key'),
  });

  const revokeKey = useMutation({
    mutationFn: (id: string) => mcpKeysApi.revoke(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.mcpKeys });
      toast.success('Key revoked');
    },
    onError: () => toast.error('Failed to revoke key'),
  });

  const keys = data?.keys ?? [];

  return (
    <SectionCard
      title="API keys"
      description="Every MCP key in one place. Mint a key here, then paste it with an endpoint URL into your MCP client."
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Workspace endpoint</Label>
          {orgEndpoint ? (
            <CopyField mono value={orgEndpoint} />
          ) : (
            <Skeleton className="h-11 w-full" />
          )}
          <p className="text-[13px] text-muted-foreground">
            Searches everything this workspace has connected.
          </p>
        </div>

        {scopedFamilies.length > 0 && workspace && (
          <div className="space-y-2">
            <Label>Or connect just what you query</Label>
            <p className="text-[13px] text-muted-foreground">
              Add the workspace endpoint above, or add the connectors you actually search below —
              not both. Every endpoint you register adds its tools to your AI client&apos;s context
              on every turn.
            </p>
            <div className="space-y-2.5">
              {scopedFamilies.map((f) => (
                <div key={f.slug} className="space-y-1">
                  <span className="text-[13px] font-medium text-label">{f.label}</span>
                  <CopyField mono value={mcpEndpoint({ slug: workspace.slug, family: f.slug })} />
                </div>
              ))}
            </div>
          </div>
        )}

        <details className="group rounded-md border border-border-subtle px-3.5 py-2.5">
          <summary className="cursor-pointer text-[13px] text-muted-foreground">
            Already connected? <code className="font-mono">/mcp</code> keeps working
          </summary>
          <p className="mt-2 text-[13px] text-muted-foreground">
            The original workspace-wide endpoint (
            <code className="font-mono">{legacyEndpoint}</code>) still works for anything connected
            before today — it infers the workspace from the key instead of the URL. New connections
            should use the workspace endpoint above.
          </p>
        </details>

        {revealedKey && (
          <Alert variant="success">
            <Key className="h-4 w-4" />
            <AlertTitle>Your API key — copy it now</AlertTitle>
            <AlertDescription className="space-y-2">
              <p className="text-[13px] text-success-foreground/80">
                This key will not be displayed again. Store it somewhere safe.
              </p>
              <CopyField mono value={revealedKey} />
              <div>
                <Button variant="ghost" size="sm" onClick={() => setRevealedKey(null)}>
                  Done
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newKeyName.trim()) createKey.mutate(newKeyName.trim());
          }}
        >
          <Input
            aria-label="Key name"
            placeholder="Key name"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
          />
          <Button type="submit" disabled={createKey.isPending || !newKeyName.trim()}>
            {createKey.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Key className="h-4 w-4" />
                Create key
              </>
            )}
          </Button>
        </form>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : keys.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">No keys yet.</p>
        ) : (
          <div className="divide-y divide-border-subtle">
            {keys.map((key) => (
              <ApiKeyRow key={key.id} apiKey={key} onRevoke={(id) => revokeKey.mutate(id)} />
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Danger zone
// ---------------------------------------------------------------------------

function DangerZone() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: wsData } = useQuery({ queryKey: qk.workspace, queryFn: () => workspaceApi.get() });
  const [confirmName, setConfirmName] = useState('');

  const wsName = wsData?.workspace.name ?? '';

  // Re-pointed from authClient.organization.delete (P0-2 fix): the server
  // endpoint enqueues the index purge BEFORE cascading the Postgres delete,
  // preventing orphaned search-index rows on a mid-delete crash.
  const deleteWs = useMutation({
    mutationFn: (name: string) => workspaceApi.delete(name),
    onSuccess: () => {
      toast.success('Workspace deleted');
      qc.clear();
      void authClient.signOut();
      void navigate({ to: '/login' });
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        (err.body as { error?: string } | null)?.error === 'confirmation_mismatch'
      ) {
        toast.error('Workspace name does not match — server rejected the confirmation');
        return;
      }
      toast.error('Delete failed. Please try again.');
    },
  });

  return (
    <SectionCard
      tone="danger"
      title="Danger zone"
      description="Irreversible actions. Read carefully before proceeding."
    >
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>Delete workspace</AlertTitle>
          <AlertDescription>
            This will permanently delete your workspace, all indexed messages, all groups, all API
            keys, and revoke all OAuth tokens. <strong>This cannot be undone.</strong> All group
            members&apos; messages stored in Brain will be purged.
          </AlertDescription>
        </Alert>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmName !== wsName) {
              toast.error('Workspace name does not match');
              return;
            }
            deleteWs.mutate(confirmName);
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="confirm-name">
              Type <strong>{wsName}</strong> to confirm
            </Label>
            <Input
              id="confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={wsName}
            />
          </div>
          <Button
            type="submit"
            variant="destructiveOutline"
            disabled={confirmName !== wsName || deleteWs.isPending || !wsName}
          >
            {deleteWs.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" /> Delete workspace permanently
              </>
            )}
          </Button>
        </form>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * Everything on this page is owner-only EXCEPT the API keys section: an MCP key
 * is per-user (mcp-keys.ts mints with the caller's userId), and this is the only
 * place to mint or revoke one, so a member who cannot reach it could never
 * connect their MCP client. Hence /settings stays in a member's nav while
 * /sources does not — see OWNER_ONLY_PATHS in top-bar.tsx.
 */
export function SettingsPage() {
  const { isOwner, roleKnown } = useActiveOrg();

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle={
          isOwner
            ? 'Manage your workspace, members, and keys.'
            : 'Manage your keys for this workspace.'
        }
      />
      <div className="mt-8 flex flex-col gap-5">
        {/* Rendered only once the role is known — the owner-only sections must
            not flash for a member on a cold load. */}
        {roleKnown && isOwner ? (
          <>
            <WorkspaceSection />
            <MembersPanel />
            <DomainPanel />
          </>
        ) : null}
        <ApiKeysSection />
        {roleKnown && isOwner ? <DangerZone /> : null}
      </div>
    </div>
  );
}
